// @ts-nocheck
import React, { useMemo, useState } from "react";
import { RacePrediction } from "@/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/analysis-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTodaysResults } from "../hooks/useTodaysResults";
import { useHotTipSettings } from "../hooks/useHotTipSettings";
import {
  formatBetType,
  formatDate,
  formatGoingCondition,
  formatStake,
} from "../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

// Group races by date and track
type GroupedPredictions = {
  [date: string]: {
    [track: string]: RacePrediction[];
  };
};

// Daily statistics interface
interface DailyStats {
  totalRaces: number;
  totalTracks: number;
  totalWinners: number;
  winRate: number;
  totalReturns: number;
  totalStake: number;
  roi: number;
  hotTipsTotal: number;
  hotTipsWon: number;
  hotTipsRate: number;
  hotTipsReturns: number;
  hotTipsStake: number;
  hotTipsRoi: number;
}

export const RacePredictions: React.FC = () => {
  const { predictions, loading, error } = useTodaysResults();
  const { hotTipMinPrice: hotTipsThreshold } = useHotTipSettings();
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [dayFilter, setDayFilter] = useState<string>("all");
  const [showOnlyHotTipRaces, setShowOnlyHotTipRaces] =
    useState<boolean>(false);

  // Group predictions by date, then by track
  const groupedByDate = useMemo(() => {
    if (!predictions || predictions.length === 0) return {};

    const grouped: GroupedPredictions = {};

    // Group by date first, then by track
    predictions.forEach((prediction) => {
      if (!prediction.date) return;

      const date = prediction.date;
      if (!grouped[date]) {
        grouped[date] = {};
      }

      const track = prediction.track || "Unknown Track";
      if (!grouped[date][track]) {
        grouped[date][track] = [];
      }

      grouped[date][track].push(prediction);
    });

    // Sort each track's races by race number
    Object.keys(grouped).forEach((date) => {
      Object.keys(grouped[date]).forEach((track) => {
        grouped[date][track].sort((a, b) => a.race - b.race);
      });
    });

    return grouped;
  }, [predictions]);

  // Get sorted dates (most recent first)
  const sortedDates = useMemo(() => {
    return Object.keys(groupedByDate).sort((a, b) => {
      // Convert DD-MM-YYYY to YYYY-MM-DD for proper sorting
      const dateA = a.split("-").reverse().join("-");
      const dateB = b.split("-").reverse().join("-");
      return dateB.localeCompare(dateA);
    });
  }, [groupedByDate]);

  // Helper function to determine if a prediction was successful
  const getPredictionResult = (prediction: RacePrediction) => {
    if (!prediction.podium || !prediction.betfairResults) return null;

    if (prediction.podium[0] === "winnerWinnerChickenDinner") {
      return {
        success: true,
        message: "Winner correctly predicted!",
        returns: prediction.winReturns || 0,
      };
    } else if (prediction.totalReturns && prediction.totalReturns > 0) {
      return {
        success: true,
        message: "Bet returned profit",
        returns: prediction.totalReturns,
      };
    } else {
      // Check if any hot tip won even though the main prediction was unsuccessful
      let hotTipWon = false;

      // Check if any of the first four horses have odds > threshold and won
      if (Array.isArray(prediction.firstFourData)) {
        for (let index = 0; index < prediction.firstFourData.length; index++) {
          const horse = prediction.firstFourData[index];
          if (
            horse &&
            isHotTip(horse.currentPrice) &&
            prediction.podium[index] === "winnerWinnerChickenDinner"
          ) {
            hotTipWon = true;
            break;
          }
        }
      }

      return {
        success: false,
        message: hotTipWon
          ? "Prediction unsuccessful but a selected hot tip won!"
          : "Prediction unsuccessful",
        returns: 0,
      };
    }
  };

  // Helper function to determine if odds qualify as a hot tip
  const isHotTip = (odds: number) => hotTipsThreshold > 0 && odds >= hotTipsThreshold;

  // Check if a prediction has any hot tips
  const predictionHasHotTip = (prediction: RacePrediction): boolean => {
    // Check if any of the first four horses have odds > threshold
    if (Array.isArray(prediction.firstFourData)) {
      for (const horse of prediction.firstFourData) {
        if (horse && isHotTip(horse.currentPrice)) {
          return true;
        }
      }
    }

    // Also check the main bet if it has odds > threshold
    const mainBetOdds = prediction.betOnValueBet?.currentOdds;
    if (mainBetOdds && isHotTip(mainBetOdds)) {
      return true;
    }

    return false;
  };

  // Filter races to show only those with hot tips if the filter is enabled
  const getFilteredRacesForTrack = (
    date: string,
    track: string,
  ): RacePrediction[] => {
    if (!showOnlyHotTipRaces) {
      return groupedByDate[date][track];
    }

    return groupedByDate[date][track].filter((prediction) =>
      predictionHasHotTip(prediction),
    );
  };

  // Calculate daily statistics
  const calculateDailyStats = (date: string): DailyStats => {
    const allPredictionsForDay: RacePrediction[] = [];
    const tracks = Object.keys(groupedByDate[date]);

    // Collect all predictions for this date
    tracks.forEach((track) => {
      allPredictionsForDay.push(...groupedByDate[date][track]);
    });

    let totalWinners = 0;
    let totalReturns = 0;
    let totalStake = 0;
    let hotTipsTotal = 0;
    let hotTipsWon = 0;
    let hotTipsReturns = 0;
    let hotTipsStake = 0;

    // Process each prediction
    allPredictionsForDay.forEach((prediction) => {
      const result = getPredictionResult(prediction);

      // Count winners and returns
      if (result?.success) {
        totalWinners++;
        totalReturns += result.returns;
      }

      // Use fixed $1 stake per race - we only bet on the first position horse in each race
      totalStake += 1; // Fixed $1 stake per race

      // Check for hot tips (odds > threshold) in any of the first four horses
      let hasHotTip = false;
      let hasWinningHotTip = false;
      let hotTipReturnsForThisRace = 0;

      // Check if any of the first four horses have odds > threshold
      if (Array.isArray(prediction.firstFourData)) {
        prediction.firstFourData.forEach((horse, index) => {
          if (horse && isHotTip(horse.currentPrice)) {
            hasHotTip = true;

            // Check if this hot tip won the race
            if (
              prediction.podium &&
              prediction.podium[index] === "winnerWinnerChickenDinner"
            ) {
              hasWinningHotTip = true;
              // If this is the winning hot tip, calculate returns based on its odds
              hotTipReturnsForThisRace = horse.currentPrice;
            }
          }
        });
      }

      // Also check the main bet if it has odds > threshold
      const mainBetOdds = prediction.betOnValueBet?.currentOdds;
      if (mainBetOdds && isHotTip(mainBetOdds)) {
        hasHotTip = true;

        // Check if the main bet won
        if (result?.success) {
          hasWinningHotTip = true;
          // If the main bet is a hot tip and won, use its returns
          hotTipReturnsForThisRace = Math.max(
            hotTipReturnsForThisRace,
            result.returns,
          );
        }
      }

      if (hasHotTip) {
        hotTipsTotal++;
        hotTipsStake += 1; // $1 stake per hot tip

        if (hasWinningHotTip) {
          hotTipsWon++;
          hotTipsReturns += hotTipReturnsForThisRace;
        }
      }
    });

    // Calculate rates and ROI
    const winRate =
      allPredictionsForDay.length > 0
        ? (totalWinners / allPredictionsForDay.length) * 100
        : 0;

    const roi =
      totalStake > 0 ? ((totalReturns - totalStake) / totalStake) * 100 : 0;

    const hotTipsRate =
      hotTipsTotal > 0 ? (hotTipsWon / hotTipsTotal) * 100 : 0;

    const hotTipsRoi =
      hotTipsStake > 0
        ? ((hotTipsReturns - hotTipsStake) / hotTipsStake) * 100
        : 0;

    return {
      totalRaces: allPredictionsForDay.length,
      totalTracks: tracks.length,
      totalWinners,
      winRate,
      totalReturns,
      totalStake,
      roi,
      hotTipsTotal,
      hotTipsWon,
      hotTipsRate,
      hotTipsReturns,
      hotTipsStake,
      hotTipsRoi,
    };
  };

  // Safe number formatting to prevent "toFixed of undefined" errors
  const safeFormat = (num: number | undefined, decimals = 2): string => {
    if (num === undefined || num === null || isNaN(num)) return "N/A";
    return num.toFixed(decimals);
  };

  // Add this helper function to check if a date is within the last 7 days
  const isWithinLastSevenDays = (dateString: string): boolean => {
    // Convert from DD-MM-YYYY to a Date object
    const [day, month, year] = dateString.split("-");
    const date = new Date(`${year}-${month}-${day}`);

    // Get date from 7 days ago
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // Reset time portion for accurate day comparison
    sevenDaysAgo.setHours(0, 0, 0, 0);

    return date >= sevenDaysAgo;
  };

  // Check if a date is on or after February 12, 2025
  const isOnOrAfterCutoffDate = (dateString: string): boolean => {
    // Convert from DD-MM-YYYY to a Date object
    const [day, month, year] = dateString.split("-");
    const date = new Date(`${year}-${month}-${day}`);

    // February 12, 2025 cutoff date
    const cutoffDate = new Date("2025-02-12");

    return date >= cutoffDate;
  };

  // Filter days based on selected criteria
  const getFilteredDates = () => {
    // First filter by cutoff date
    const datesAfterCutoff = sortedDates.filter(isOnOrAfterCutoffDate);

    if (dayFilter === "all") return datesAfterCutoff;

    return datesAfterCutoff.filter((date) => {
      const stats = calculateDailyStats(date);

      switch (dayFilter) {
        case "hotTips":
          return stats.hotTipsTotal > 0;
        case "winners":
          return stats.totalWinners > 0;
        case "highROI":
          return stats.roi > 20;
        case "positiveROI":
          return stats.roi > 0;
        case "hotTipWinners":
          return stats.hotTipsWon > 0;
        default:
          return true;
      }
    });
  };

  const filteredDates = getFilteredDates();

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader className="bg-red-50 border-b border-red-100">
          <CardTitle className="text-red-600">
            Error Loading Predictions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-alert-circle"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p>{error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="space-y-8">
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center space-y-4">
              <div className="relative w-16 h-16">
                <div className="absolute top-0 left-0 w-full h-full border-4 border-gray-200 rounded-full"></div>
                <div className="absolute top-0 left-0 w-full h-full border-4 border-t-primary border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
              </div>
              <p className="text-muted-foreground">
                Loading prediction results...
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Skeleton className="h-10 w-1/4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-64 w-full rounded-lg" />
              <Skeleton className="h-64 w-full rounded-lg" />
            </div>
          </div>
        </div>
      ) : filteredDates.length === 0 ? (
        <Card className="w-full">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-muted-foreground"
              >
                <rect width="18" height="18" x="3" y="3" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">No predictions found</h3>
            <p className="text-muted-foreground">
              Check back later for racing predictions
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Top Tips Threshold Selector */}
          <Card className="w-full">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2 text-amber-500"
                    >
                      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                    </svg>
                    Top Tips Settings
                  </CardTitle>
                  <CardDescription>
                    Adjust the minimum odds threshold for hot tips
                  </CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">
                    Minimum Odds: ${hotTipsThreshold}+ {hotTipsThreshold === 0 ? '(disabled)' : ''}
                  </span>
                  <span className="text-xs text-muted-foreground ml-2">
                    (Configure in Dashboard)
                  </span>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Day Filter */}
          <Card className="w-full">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2 text-blue-500"
                    >
                      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                    </svg>
                    Filter Options
                  </CardTitle>
                  <CardDescription>
                    Filter days and races based on specific criteria
                  </CardDescription>
                </div>
                <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4 sm:items-center">
                  <div className="flex items-center space-x-2">
                    <Select
                      value={dayFilter}
                      onValueChange={(value: string) => setDayFilter(value)}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter days" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Days</SelectItem>
                        <SelectItem value="hotTips">
                          Days with Top Tips
                        </SelectItem>
                        <SelectItem value="winners">
                          Days with Winners
                        </SelectItem>
                        <SelectItem value="highROI">
                          Days with High ROI (&gt;20%)
                        </SelectItem>
                        <SelectItem value="positiveROI">
                          Days with Positive ROI
                        </SelectItem>
                        <SelectItem value="hotTipWinners">
                          Days with Top Tip Winners
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                        checked={showOnlyHotTipRaces}
                        onChange={(e) =>
                          setShowOnlyHotTipRaces(e.target.checked)
                        }
                      />
                      <span className="text-sm font-medium">
                        Show only races with hot tips
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-2 p-3 bg-blue-50 text-blue-800 rounded-md text-sm">
                <div className="flex items-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mr-2 mt-0.5 flex-shrink-0"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4" />
                    <path d="M12 8h.01" />
                  </svg>
                  <span>
                    <strong>Note:</strong> Only data from February 12, 2025
                    onwards is displayed as earlier data is no longer relevant.
                  </span>
                </div>
              </div>
              {filteredDates.length === 0 && (
                <div className="mt-2 p-3 bg-amber-50 text-amber-800 rounded-md text-sm">
                  No days match the selected filter. Try a different filter.
                </div>
              )}
            </CardHeader>
          </Card>

          {/* Overall Statistics Summary */}
          <Card className="w-full overflow-hidden bg-white shadow-lg border-none">
            <CardHeader className="pb-2 bg-gradient-to-r from-primary/10 to-primary/5 border-b">
              <CardTitle className="text-xl flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2 text-primary"
                >
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
                Last 7 Days Performance Summary
              </CardTitle>
              <CardDescription>
                Aggregated statistics across predictions from the past 7 days
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {(() => {
                // Filter dates within the last 7 days
                const recentDates = filteredDates.filter(isWithinLastSevenDays);

                if (recentDates.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground flex flex-col items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mb-4 text-muted"
                      >
                        <rect width="18" height="18" x="3" y="3" rx="2" />
                        <path d="M3 9h18" />
                        <path d="M9 21V9" />
                      </svg>
                      <p className="text-lg font-medium mb-1">
                        No recent data available
                      </p>
                      <p>No prediction data available for the past 7 days</p>
                    </div>
                  );
                }

                // Calculate overall statistics for the last 7 days
                let totalRaces = 0;
                let totalWinners = 0;
                let totalReturns = 0;
                let totalStake = 0;
                let hotTipsTotal = 0;
                let hotTipsWon = 0;
                let hotTipsReturns = 0;
                let hotTipsStake = 0;

                // Process all predictions across recent dates
                recentDates.forEach((date) => {
                  const stats = calculateDailyStats(date);
                  totalRaces += stats.totalRaces;
                  totalWinners += stats.totalWinners;
                  totalReturns += stats.totalReturns;
                  totalStake += stats.totalStake;
                  hotTipsTotal += stats.hotTipsTotal;
                  hotTipsWon += stats.hotTipsWon;
                  hotTipsReturns += stats.hotTipsReturns;
                  hotTipsStake += stats.hotTipsStake;
                });

                // Calculate overall rates
                const winRate =
                  totalRaces > 0 ? (totalWinners / totalRaces) * 100 : 0;
                const roi =
                  totalStake > 0
                    ? ((totalReturns - totalStake) / totalStake) * 100
                    : 0;
                const hotTipsRate =
                  hotTipsTotal > 0 ? (hotTipsWon / hotTipsTotal) * 100 : 0;
                const hotTipsRoi =
                  hotTipsStake > 0
                    ? ((hotTipsReturns - hotTipsStake) / hotTipsStake) * 100
                    : 0;

                return (
                  <div>
                    {/* Summary badges at the top */}
                    <div className="flex flex-wrap gap-3 mb-6">
                      <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-medium shadow-sm">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mr-2"
                        >
                          <path d="M19 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Z" />
                          <path d="M12 12h.01" />
                        </svg>
                        {totalRaces} Races
                      </div>

                      <div
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium shadow-sm ${
                          winRate > 30
                            ? "bg-green-50 text-green-700"
                            : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mr-2"
                        >
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <path d="m9 11 3 3L22 4" />
                        </svg>
                        {safeFormat(winRate)}% Win Rate
                      </div>

                      <div
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium shadow-sm ${
                          roi >= 0
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mr-2"
                        >
                          <path d="M12 2v20" />
                          <path d="m17 5-5-3-5 3" />
                          <path d="m17 19-5 3-5-3" />
                        </svg>
                        ROI: {safeFormat(roi)}%
                      </div>

                      {hotTipsTotal > 0 && (
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-50 text-amber-700 text-sm font-medium shadow-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-2"
                          >
                            <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                          </svg>
                          {hotTipsWon}/{hotTipsTotal} Top Tips •{" "}
                          {safeFormat(hotTipsRate)}% Success ($
                          {safeFormat(hotTipsStake)} → $
                          {safeFormat(hotTipsReturns)}, ROI:{" "}
                          {safeFormat(hotTipsRoi)}%)
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                      <div className="bg-white rounded-xl p-5 shadow-md flex flex-col border border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-primary text-sm font-medium">
                            Total Races
                          </span>
                          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-blue-600"
                            >
                              <path d="M19 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Z" />
                              <path d="M12 12h.01" />
                            </svg>
                          </div>
                        </div>
                        <span className="text-3xl font-bold mt-2">
                          {totalRaces}
                        </span>
                        <span className="text-muted-foreground text-sm mt-1">
                          Past 7 days ({recentDates.length} days)
                        </span>
                      </div>

                      <div className="bg-white rounded-xl p-5 shadow-md flex flex-col border border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-green-600 text-sm font-medium">
                            Success Rate
                          </span>
                          <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-green-600"
                            >
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                              <path d="m9 11 3 3L22 4" />
                            </svg>
                          </div>
                        </div>
                        <span className="text-3xl font-bold mt-2">
                          {safeFormat(winRate)}%
                        </span>
                        <span className="text-muted-foreground text-sm mt-1">
                          {totalWinners} winning predictions
                        </span>
                      </div>

                      <div className="bg-white rounded-xl p-5 shadow-md flex flex-col border border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-purple-600 text-sm font-medium">
                            Total Returns
                          </span>
                          <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-purple-600"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                              <path d="M12 18V6" />
                            </svg>
                          </div>
                        </div>
                        <span className="text-3xl font-bold mt-2">
                          ${safeFormat(totalReturns)}
                        </span>
                        <span
                          className={`text-sm mt-1 ${
                            roi >= 0 ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          ROI: {safeFormat(roi)}%
                        </span>
                      </div>

                      {hotTipsTotal > 0 && (
                        <div className="bg-white rounded-xl p-5 shadow-md flex flex-col border border-gray-100">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-amber-600 text-sm font-medium">
                              Top Tips Success
                            </span>
                            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-amber-600"
                              >
                                <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                              </svg>
                            </div>
                          </div>
                          <span className="text-3xl font-bold mt-2 flex items-baseline">
                            {safeFormat(hotTipsRate)}%
                            <span className="text-sm text-muted-foreground ml-2">
                              Success Rate
                            </span>
                          </span>
                          <div className="space-y-1 mt-2">
                            <div className="text-sm">
                              <span className="text-muted-foreground">
                                Bets:
                              </span>{" "}
                              {hotTipsWon}/{hotTipsTotal} winners
                            </div>
                            <div className="text-sm">
                              <span className="text-muted-foreground">
                                Investment:
                              </span>{" "}
                              ${safeFormat(hotTipsStake)} → $
                              {safeFormat(hotTipsReturns)}
                            </div>
                            <div
                              className={`text-sm ${
                                hotTipsRoi >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              } font-medium`}
                            >
                              ROI: {safeFormat(hotTipsRoi)}%
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Overall progress bar */}
                    <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-base font-medium flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-2"
                          >
                            <path d="M12 2v20" />
                            <path d="m17 5-5-3-5 3" />
                            <path d="m17 19-5 3-5-3" />
                          </svg>
                          7-Day ROI
                        </span>
                        <span
                          className={`text-base font-medium ${
                            roi >= 0 ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {safeFormat(roi)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
                        <div
                          className={`${
                            roi >= 0 ? "bg-green-600" : "bg-red-600"
                          } h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                          style={{
                            width: `${Math.min(Math.abs(roi) + 5, 100)}%`,
                          }}
                        >
                          {Math.abs(roi) > 15 && (
                            <span className="text-white text-xs font-medium">
                              {safeFormat(roi)}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-1"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 8v4l3 3" />
                          </svg>
                          <span>Based on fixed $1 stake per race</span>
                        </div>
                        <div className="flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-1"
                          >
                            <path d="M12 2v20" />
                            <path d="m17 5-5-3-5 3" />
                            <path d="m17 19-5 3-5-3" />
                          </svg>
                          <span>
                            ${safeFormat(totalStake)} stake → $
                            {safeFormat(totalReturns)} returns
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {filteredDates.map((date) => (
            <Card key={date} className="w-full overflow-hidden">
              <CardHeader
                className={`bg-primary/5 cursor-pointer transition-colors hover:bg-primary/10`}
                onClick={() =>
                  setExpandedDate(expandedDate === date ? null : date)
                }
              >
                <div className="flex justify-between items-center">
                  <CardTitle className="text-xl">{formatDate(date)}</CardTitle>
                  <div className="text-muted-foreground">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`transition-transform ${
                        expandedDate === date ? "rotate-180" : ""
                      }`}
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                </div>
              </CardHeader>

              {expandedDate === date && (
                <CardContent className="p-6">
                  {/* Daily Statistics Dashboard */}
                  <div className="mb-8 bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="bg-primary/10 p-4 border-b">
                      <h3 className="text-lg font-medium flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mr-2"
                        >
                          <path d="M3 3v18h18" />
                          <path d="m19 9-5 5-4-4-3 3" />
                        </svg>
                        Daily Performance Summary
                      </h3>
                    </div>

                    {(() => {
                      const stats = calculateDailyStats(date);
                      return (
                        <div className="p-5">
                          {/* Summary badges at the top */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="mr-1"
                              >
                                <path d="M19 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Z" />
                                <path d="M12 12h.01" />
                              </svg>
                              {stats.totalRaces} Races
                            </div>

                            <div
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                                stats.winRate > 30
                                  ? "bg-green-100 text-green-800"
                                  : "bg-amber-100 text-amber-800"
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="mr-1"
                              >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <path d="m9 11 3 3L22 4" />
                              </svg>
                              {safeFormat(stats.winRate)}% Win Rate
                            </div>

                            <div
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                                stats.roi >= 0
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="mr-1"
                              >
                                <path d="M12 2v20" />
                                <path d="m17 5-5-3-5 3" />
                                <path d="m17 19-5 3-5-3" />
                              </svg>
                              ROI: {safeFormat(stats.roi)}%
                            </div>

                            {stats.hotTipsTotal > 0 && (
                              <div className="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-sm">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                                </svg>
                                {stats.hotTipsWon}/{stats.hotTipsTotal} Top Tips
                                (${safeFormat(stats.hotTipsStake)} → $
                                {safeFormat(stats.hotTipsReturns)})
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {/* Overview Stats */}
                            <div className="bg-blue-50 rounded-lg p-4 flex flex-col shadow-sm hover:shadow-md transition-shadow">
                              <span className="text-blue-500 text-sm font-medium flex items-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <rect
                                    width="18"
                                    height="18"
                                    x="3"
                                    y="3"
                                    rx="2"
                                  />
                                  <path d="M3 9h18" />
                                  <path d="M9 21V9" />
                                </svg>
                                Overview
                              </span>
                              <div className="mt-2 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Races:
                                  </span>
                                  <span className="font-semibold">
                                    {stats.totalRaces}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Tracks:
                                  </span>
                                  <span className="font-semibold">
                                    {stats.totalTracks}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Performance Stats */}
                            <div className="bg-green-50 rounded-lg p-4 flex flex-col shadow-sm hover:shadow-md transition-shadow">
                              <span className="text-green-500 text-sm font-medium flex items-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                  <path d="m9 11 3 3L22 4" />
                                </svg>
                                Performance
                              </span>
                              <div className="mt-2 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Winners:
                                  </span>
                                  <span className="font-semibold">
                                    {stats.totalWinners}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Win Rate:
                                  </span>
                                  <span className="font-semibold">
                                    {safeFormat(stats.winRate)}%
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Financial Stats */}
                            <div className="bg-purple-50 rounded-lg p-4 flex flex-col shadow-sm hover:shadow-md transition-shadow">
                              <span className="text-purple-500 text-sm font-medium flex items-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                                  <path d="M12 18V6" />
                                </svg>
                                Returns
                              </span>
                              <div className="mt-2 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Total:
                                  </span>
                                  <span className="font-semibold">
                                    ${safeFormat(stats.totalReturns)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    ROI:
                                  </span>
                                  <span
                                    className={`font-semibold ${
                                      stats.roi >= 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {safeFormat(stats.roi)}%
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Top Tips Stats */}
                            <div className="bg-amber-50 rounded-lg p-4 flex flex-col shadow-sm hover:shadow-md transition-shadow">
                              <span className="text-amber-500 text-sm font-medium flex items-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                                </svg>
                                Top Tips (Odds {">"} ${hotTipsThreshold})
                              </span>
                              <div className="mt-2 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Total Tips:
                                  </span>
                                  <span className="font-semibold">
                                    {stats.hotTipsTotal}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Success:
                                  </span>
                                  <span className="font-semibold">
                                    {stats.hotTipsWon}/{stats.hotTipsTotal} (
                                    {safeFormat(stats.hotTipsRate)}%)
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Total Bet:
                                  </span>
                                  <span className="font-semibold">
                                    ${safeFormat(stats.hotTipsStake)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Returns:
                                  </span>
                                  <span className="font-semibold">
                                    ${safeFormat(stats.hotTipsReturns)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    ROI:
                                  </span>
                                  <span
                                    className={`font-semibold ${
                                      stats.hotTipsRoi >= 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {safeFormat(stats.hotTipsRoi)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Progress bars for visual representation */}
                          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-4 rounded-lg shadow-sm">
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium flex items-center">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="mr-1"
                                  >
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <path d="m9 11 3 3L22 4" />
                                  </svg>
                                  Win Rate
                                </span>
                                <span className="text-sm font-medium">
                                  {safeFormat(stats.winRate)}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                <div
                                  className="bg-green-600 h-2.5 rounded-full transition-all duration-500"
                                  style={{
                                    width: `${Math.min(stats.winRate, 100)}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                {stats.totalWinners} winners from{" "}
                                {stats.totalRaces} races
                              </div>
                            </div>

                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium flex items-center">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="mr-1"
                                  >
                                    <path d="M12 2v20" />
                                    <path d="m17 5-5-3-5 3" />
                                    <path d="m17 19-5 3-5-3" />
                                  </svg>
                                  ROI
                                </span>
                                <span
                                  className={`text-sm font-medium ${
                                    stats.roi >= 0
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {safeFormat(stats.roi)}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                <div
                                  className={`${
                                    stats.roi >= 0
                                      ? "bg-green-600"
                                      : "bg-red-600"
                                  } h-2.5 rounded-full transition-all duration-500`}
                                  style={{
                                    width: `${Math.min(
                                      Math.abs(stats.roi),
                                      100,
                                    )}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                ${safeFormat(stats.totalReturns)} returns on $
                                {safeFormat(stats.totalStake)} stake ($1 per
                                race, betting only on predicted winner)
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="space-y-8">
                    {Object.keys(groupedByDate[date]).map((track) => (
                      <div key={`${date}-${track}`} className="space-y-4">
                        <h2 className="text-xl font-bold border-b pb-2 flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-2"
                          >
                            <path d="M19 7H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Z" />
                            <path d="M17 2H7a1 1 0 0 0-1 1v4h12V3a1 1 0 0 0-1-1Z" />
                            <path d="M12 10v6" />
                            <path d="m9 13 3-3 3 3" />
                          </svg>
                          {track}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {getFilteredRacesForTrack(date, track).map(
                            (prediction) => {
                              const result = getPredictionResult(prediction);

                              return (
                                <div
                                  key={`${prediction.track}-${prediction.race}-${prediction.date}`}
                                  className={`border rounded-lg p-5 bg-card hover:shadow-md transition-all ${
                                    result?.success
                                      ? "border-green-500 shadow-green-100"
                                      : result === null
                                        ? "border-gray-200"
                                        : "border-red-300 shadow-red-50"
                                  }`}
                                >
                                  <div className="flex justify-between items-start mb-4">
                                    <div>
                                      <h3 className="text-lg font-semibold flex items-center">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="18"
                                          height="18"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="mr-1"
                                        >
                                          <path d="M19 6V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v1" />
                                          <path d="M3 6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2H3Z" />
                                          <path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
                                          <path d="M10 12h4" />
                                        </svg>
                                        Race {prediction.race}
                                      </h3>
                                      <div className="text-sm text-muted-foreground flex items-center mt-1">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="14"
                                          height="14"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="mr-1"
                                        >
                                          <path d="M2 12a10 10 0 1 0 20 0 10 10 0 1 0-20 0Z" />
                                          <path d="M12 6v6l4 2" />
                                        </svg>
                                        {formatGoingCondition(prediction.going)}
                                      </div>
                                    </div>
                                    {prediction.valueBet === "BET" && (
                                      <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                                        VALUE BET
                                      </div>
                                    )}
                                  </div>

                                  {/* Result banner if available */}
                                  {result && (
                                    <div
                                      className={`mb-4 p-3 rounded-md text-sm font-medium flex items-center ${
                                        result.success
                                          ? "bg-green-100 text-green-800"
                                          : result.message.includes(
                                                "hot tip won",
                                              )
                                            ? "bg-amber-100 text-amber-800"
                                            : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="mr-2"
                                      >
                                        {result.success ? (
                                          <>
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                            <path d="m9 11 3 3L22 4" />
                                          </>
                                        ) : (
                                          <>
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="m15 9-6 6" />
                                            <path d="m9 9 6 6" />
                                          </>
                                        )}
                                      </svg>
                                      <div>
                                        {result.message}
                                        {result.returns > 0 && (
                                          <span className="ml-2 font-bold">
                                            Returns: $
                                            {safeFormat(result.returns)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  <div className="mb-4">
                                    <div className="text-sm font-semibold mb-1 flex items-center">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="mr-1"
                                      >
                                        <path d="M18 8c0 4.5-6 9-6 9s-6-4.5-6-9a6 6 0 0 1 12 0" />
                                        <circle cx="12" cy="8" r="2" />
                                      </svg>
                                      Most Likely Winner (Our $1 Bet)
                                    </div>
                                    <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-md">
                                      <div className="font-medium">
                                        {prediction.mostLikelyWinner}
                                        {prediction.podium &&
                                          prediction.podium[0] ===
                                            "winnerWinnerChickenDinner" && (
                                            <span className="ml-2 text-amber-500">
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="inline"
                                              >
                                                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                                                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                                                <path d="M4 22h16" />
                                                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                                                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                                                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                                              </svg>
                                              Winner!
                                            </span>
                                          )}
                                      </div>
                                      {prediction.betfairResults?.winner && (
                                        <div className="text-xs bg-secondary px-2 py-1 rounded-full ml-auto">
                                          Starting Odds: $
                                          {
                                            prediction.betfairResults.winner
                                              .startingPrice
                                          }
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {Array.isArray(prediction.firstFour) &&
                                    prediction.firstFour.length > 0 && (
                                      <div className="mb-4">
                                        <div className="text-sm font-semibold mb-1 flex items-center">
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="mr-1"
                                          >
                                            <path d="M16 18a4 4 0 0 0-8 0" />
                                            <circle cx="12" cy="11" r="3" />
                                            <rect
                                              width="18"
                                              height="18"
                                              x="3"
                                              y="3"
                                              rx="2"
                                            />
                                            <path d="M3 9h18" />
                                          </svg>
                                          First Four Prediction (Only bet on #1)
                                        </div>
                                        <div className="space-y-2 mt-2">
                                          {prediction.firstFour.map(
                                            (horse, index) => {
                                              const podiumResult =
                                                prediction.podium?.[index];
                                              const resultClass =
                                                podiumResult ===
                                                "winnerWinnerChickenDinner"
                                                  ? "bg-green-100 text-green-800"
                                                  : podiumResult === "PL"
                                                    ? "bg-blue-100 text-blue-800"
                                                    : "bg-gray-50";

                                              return (
                                                <div
                                                  key={index}
                                                  className={`flex items-center gap-2 p-2 rounded-md ${resultClass}`}
                                                >
                                                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">
                                                    {index + 1}
                                                  </div>
                                                  <div className="flex-1">
                                                    {horse}
                                                    {podiumResult ===
                                                      "winnerWinnerChickenDinner" && (
                                                      <span className="ml-2 text-amber-500">
                                                        <svg
                                                          xmlns="http://www.w3.org/2000/svg"
                                                          width="16"
                                                          height="16"
                                                          viewBox="0 0 24 24"
                                                          fill="none"
                                                          stroke="currentColor"
                                                          strokeWidth="2"
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          className="inline"
                                                        >
                                                          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                                                          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                                                          <path d="M4 22h16" />
                                                          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                                                          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                                                          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                                                        </svg>
                                                      </span>
                                                    )}
                                                  </div>
                                                  {Array.isArray(
                                                    prediction.firstFourData,
                                                  ) &&
                                                    prediction.firstFourData[
                                                      index
                                                    ] && (
                                                      <div className="text-sm font-medium flex items-center">
                                                        Odds: $
                                                        {safeFormat(
                                                          prediction
                                                            .firstFourData[
                                                            index
                                                          ]?.currentPrice,
                                                        )}
                                                        {hotTipsThreshold > 0 &&
                                                          prediction
                                                          .firstFourData[index]
                                                          ?.currentPrice >
                                                          hotTipsThreshold && (
                                                          <span className="ml-1 text-amber-500">
                                                            <svg
                                                              xmlns="http://www.w3.org/2000/svg"
                                                              width="14"
                                                              height="14"
                                                              viewBox="0 0 24 24"
                                                              fill="none"
                                                              stroke="currentColor"
                                                              strokeWidth="2"
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                            >
                                                              <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                                                            </svg>
                                                          </span>
                                                        )}
                                                      </div>
                                                    )}
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      </div>
                                    )}

                                  <div>
                                    <div className="text-sm font-semibold mb-1 flex items-center">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="mr-1"
                                      >
                                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                                      </svg>
                                      Analysis
                                    </div>
                                    <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-md">
                                      {prediction.decisionExplanation}
                                    </p>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}

              {expandedDate !== date && (
                <CardFooter className="p-4 bg-muted/10 border-t">
                  <div className="text-sm text-muted-foreground">
                    Click to view {Object.keys(groupedByDate[date]).length}{" "}
                    tracks and{" "}
                    {Object.values(groupedByDate[date]).reduce(
                      (acc, trackRaces) => acc + trackRaces.length,
                      0,
                    )}{" "}
                    races
                  </div>
                </CardFooter>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
